import {
  Alert,
  Anchor,
  Button,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { LuCalendar, LuFileText, LuLogOut, LuTriangleAlert, LuUser } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { EmptyState } from '@/frontend/components/marina/EmptyState'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { PageLoader } from '@/frontend/components/PageLoader'
import { useLogout } from '@/frontend/hooks/useAuth'

export const Route = createFileRoute('/portal/')({
  beforeLoad: async ({ context }) => {
    try {
      const data = await context.queryClient.ensureQueryData({
        queryKey: ['auth', 'session'],
        queryFn: () => fetch('/api/auth/session', { credentials: 'include' }).then((r) => r.json()),
      })
      if (!data?.user) throw redirect({ to: '/login' })
      if (data.user.blocked) throw redirect({ to: '/blocked' })
      if (data.user.role === 'PENDING') throw redirect({ to: '/pending' })
      if (data.user.role === 'SUPER_ADMIN') throw redirect({ to: '/dev' as any })
      if (['MANAGER', 'STAFF', 'VIEWER'].includes(data.user.role)) throw redirect({ to: '/dashboard' })
    } catch (e) {
      if (e instanceof Error) throw redirect({ to: '/login' })
      throw e
    }
  },
  component: PortalHome,
})

type PortalCustomer = {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  category: string
  _count: { bookings: number; invoices: number; boats: number }
}

type PortalBooking = {
  id: string
  code: string
  status: string
  checkInAt: string
  checkOutAt: string
  boat: { id: string; name: string }
  berth: { code: string } | null
}

type PortalInvoice = {
  id: string
  code: string
  status: string
  totalIdr: string
  paidIdr: string
  dueDate: string
  issueDate: string
  boat: { id: string; name: string } | null
}

async function api<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function PortalHome() {
  const logout = useLogout()
  const me = useQuery({
    queryKey: ['portal', 'me'],
    queryFn: () => api<{ customer: PortalCustomer } | { error: string; hint: string }>('/api/portal/me'),
    retry: false,
  })
  const bookings = useQuery({
    queryKey: ['portal', 'bookings'],
    queryFn: () => api<{ items: PortalBooking[] }>('/api/portal/my-bookings'),
  })
  const invoices = useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn: () => api<{ items: PortalInvoice[] }>('/api/portal/my-invoices'),
  })

  if (me.isLoading) return <PageLoader />

  if (me.error || !me.data || 'error' in me.data) {
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Alert color="karang" icon={<LuTriangleAlert size={18} />} title="Profil Pelanggan Belum Terhubung">
            Email akun Anda belum cocok dengan customer manapun di sistem marina ini. Mohon hubungi staff marina untuk
            menambahkan email Anda agar bisa melihat reservasi & invoice di portal.
          </Alert>
          <Button leftSection={<LuLogOut size={16} />} variant="default" onClick={() => logout.mutate()}>
            Keluar
          </Button>
        </Stack>
      </Container>
    )
  }

  const customer = me.data.customer
  const upcomingBookings = (bookings.data?.items ?? []).filter((b) => dayjs(b.checkInAt).isAfter(dayjs()))
  const allInvoices = invoices.data?.items ?? []
  const openInvoices = allInvoices.filter(
    (i) => i.status === 'SENT' || i.status === 'PARTIALLY_PAID' || i.status === 'OVERDUE',
  )
  const totalOutstanding = openInvoices.reduce((sum, i) => sum + Math.max(0, Number(i.totalIdr) - Number(i.paidIdr)), 0)

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Portal Pelanggan
            </Text>
            <Title order={2} ff="var(--font-display)" fw={600}>
              Halo, {customer.firstName} 👋
            </Title>
            <Text c="dimmed" size="sm">
              {customer.email ?? '—'}
              {customer.phone ? ` · ${customer.phone}` : ''}
            </Text>
          </div>
          <Group gap="xs">
            <Button
              component={Link}
              to="/portal/profile"
              variant="default"
              leftSection={<LuUser size={16} />}
              size="sm"
            >
              Profile
            </Button>
            <Button leftSection={<LuLogOut size={16} />} variant="default" size="sm" onClick={() => logout.mutate()}>
              Keluar
            </Button>
          </Group>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <StatCard icon={<LuCalendar size={20} />} label="Reservasi" value={customer._count.bookings} />
          <StatCard icon={<LuFileText size={20} />} label="Invoice" value={customer._count.invoices} />
          <StatCard
            icon={<LuTriangleAlert size={20} />}
            label="Outstanding"
            value={<CurrencyText idr={totalOutstanding} />}
            color={totalOutstanding > 0 ? 'red' : 'teal'}
          />
        </SimpleGrid>

        <Card padding="lg" radius="md">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm">
            Reservasi Mendatang
          </Text>
          {upcomingBookings.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" py="md">
              Tidak ada reservasi mendatang
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kode</Table.Th>
                  <Table.Th>Kapal</Table.Th>
                  <Table.Th>Check-in</Table.Th>
                  <Table.Th>Berth</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {upcomingBookings.slice(0, 5).map((b) => (
                  <Table.Tr key={b.id}>
                    <Table.Td fw={500}>{b.code}</Table.Td>
                    <Table.Td>{b.boat.name}</Table.Td>
                    <Table.Td>{dayjs(b.checkInAt).format('DD MMM YYYY')}</Table.Td>
                    <Table.Td>{b.berth?.code ?? '—'}</Table.Td>
                    <Table.Td>
                      <StatusBadge status={b.status} kind="booking" upper />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Card padding="lg" radius="md">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="sm">
            Invoice
          </Text>
          {allInvoices.length === 0 ? (
            <EmptyState
              kind="buoy"
              size="sm"
              backdrop={false}
              title="Belum ada invoice"
              description="Tagihan dan pembayaran Anda akan tampil di sini."
            />
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Kode</Table.Th>
                  <Table.Th>Jatuh Tempo</Table.Th>
                  <Table.Th ta="right">Total</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Aksi</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {allInvoices.slice(0, 8).map((inv) => (
                  <Table.Tr key={inv.id}>
                    <Table.Td fw={500}>{inv.code}</Table.Td>
                    <Table.Td>{dayjs(inv.dueDate).format('DD MMM YYYY')}</Table.Td>
                    <Table.Td ta="right">
                      <CurrencyText idr={inv.totalIdr} fw={600} />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={inv.status} kind="invoice" upper />
                    </Table.Td>
                    <Table.Td ta="right">
                      <Anchor href={`/api/portal/my-invoices/${inv.id}/pdf`} target="_blank" c="laut" size="sm">
                        PDF
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>
    </Container>
  )
}

function StatCard({
  icon,
  label,
  value,
  color = 'teal',
}: {
  icon: React.ReactNode
  label: string
  value: number | React.ReactNode
  color?: string
}) {
  return (
    <Card padding="lg" radius="md">
      <Group gap="xs" mb={4}>
        <ThemeIcon variant="light" color={color} size="md" radius="md">
          {icon}
        </ThemeIcon>
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
      </Group>
      <Text fw={700} fz="2rem" lh={1}>
        {value}
      </Text>
    </Card>
  )
}
